import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { authAxios } from "../../utils/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Search,
  ChevronDown,
  ChevronRight,
  Copy,
  FileText,
  Cpu,
  Hash,
  ExternalLink,
  Target,
  X,
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Check,
  RotateCcw,
  Save,
  Filter
} from "lucide-react";

export default function SystemPrompts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Agent filter state
  const [selectedAgent, setSelectedAgent] = useState("all");
  const [allAgents, setAllAgents] = useState([]);
  
  // Editor modal state
  const [selectedPromptId, setSelectedPromptId] = useState(null);
  const [promptDetail, setPromptDetail] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [selectedRevision, setSelectedRevision] = useState("default");
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [editName, setEditName] = useState("");
  const [editRole, setEditRole] = useState("");
  const [editPurpose, setEditPurpose] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Create revision state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newRevisionName, setNewRevisionName] = useState("");
  const [creating, setCreating] = useState(false);
  
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await authAxios.get("/system-prompts/categories");
      if (response.data.success) {
        setCategories(response.data.categories);
        if (response.data.categories.length > 0) {
          setExpandedCategories({ [response.data.categories[0].id]: true });
        }
        
        // Extract unique agent names from all prompts
        const agents = new Set();
        response.data.categories.forEach(category => {
          category.prompts.forEach(prompt => {
            if (prompt.agent_name) {
              agents.add(prompt.agent_name);
            }
          });
        });
        // Sort A-Z case-insensitive
        setAllAgents(Array.from(agents).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())));
      }
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      toast.error("Failed to load system prompts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Check for promptId or agent in URL params
  useEffect(() => {
    const promptIdParam = searchParams.get("promptId");
    const agentParam = searchParams.get("agent");
    
    if (promptIdParam && !loading) {
      openPromptEditor(promptIdParam);
      // Clear the param after opening
      searchParams.delete("promptId");
      setSearchParams(searchParams, { replace: true });
    }
    
    if (agentParam && !loading) {
      setSelectedAgent(agentParam);
      // Clear the param after setting
      searchParams.delete("agent");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, loading]);

  // Filter categories based on selected agent
  const filteredCategories = useMemo(() => {
    if (selectedAgent === "all") return categories;
    
    return categories.map(category => ({
      ...category,
      prompts: category.prompts.filter(prompt => prompt.agent_name === selectedAgent)
    })).filter(category => category.prompts.length > 0);
  }, [categories, selectedAgent]);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }

    setSearching(true);
    try {
      const response = await authAxios.get(`/system-prompts/search?q=${encodeURIComponent(searchQuery)}`);
      if (response.data.success) {
        setSearchResults(response.data);
      }
    } catch (error) {
      console.error("Search failed:", error);
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults(null);
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const openPromptEditor = async (promptId) => {
    setSelectedPromptId(promptId);
    setLoadingPrompt(true);
    setIsEditing(false);
    setShowCreateForm(false);
    
    try {
      // Fetch prompt detail and revisions in parallel
      const [detailRes, revisionsRes] = await Promise.all([
        authAxios.get(`/system-prompts/prompt/${promptId}`),
        authAxios.get(`/system-prompts/prompt/${promptId}/revisions`)
      ]);
      
      if (detailRes.data.success) {
        setPromptDetail(detailRes.data.prompt);
      }
      
      if (revisionsRes.data.success) {
        setRevisions(revisionsRes.data.revisions);
        
        // Determine which revision is active and load its content
        let activeRevisionId = "default";
        if (!revisionsRes.data.default.is_active) {
          const active = revisionsRes.data.revisions.find(r => r.is_active);
          if (active) {
            activeRevisionId = active.id;
          }
        }
        
        setSelectedRevision(activeRevisionId);
        
        // If the active revision is not default, fetch its full content
        if (activeRevisionId !== "default") {
          try {
            const contentRes = await authAxios.get(`/system-prompts/prompt/${promptId}/revisions/${activeRevisionId}`);
            if (contentRes.data.success) {
              setRevisions(prev => prev.map(r => 
                r.id === activeRevisionId ? { ...r, content: contentRes.data.revision.content } : r
              ));
            }
          } catch (error) {
            console.error("Failed to fetch active revision content:", error);
          }
        }
      }
    } catch (error) {
      console.error("Failed to fetch prompt:", error);
      toast.error("Failed to load prompt details");
      closeEditor();
    } finally {
      setLoadingPrompt(false);
    }
  };

  const closeEditor = () => {
    setSelectedPromptId(null);
    setPromptDetail(null);
    setRevisions([]);
    setSelectedRevision("default");
    setIsEditing(false);
    setShowCreateForm(false);
    setEditContent("");
    setEditName("");
  };

  const getSelectedContent = () => {
    if (selectedRevision === "default") {
      return promptDetail?.default_content || "";
    }
    const rev = revisions.find(r => r.id === selectedRevision);
    return rev?.content || "";
  };

  const isDefaultSelected = selectedRevision === "default";
  
  const isActiveRevision = (revId) => {
    if (revId === "default") {
      return !revisions.some(r => r.is_active);
    }
    const rev = revisions.find(r => r.id === revId);
    return rev?.is_active || false;
  };

  const handleRevisionSelect = async (revId) => {
    if (revId === selectedRevision) return;
    
    // If we have unsaved edits, warn user
    if (isEditing) {
      if (!window.confirm("You have unsaved changes. Discard them?")) {
        return;
      }
      setIsEditing(false);
    }
    
    setSelectedRevision(revId);
    
    // Fetch full content if it's a custom revision
    if (revId !== "default") {
      try {
        const response = await authAxios.get(`/system-prompts/prompt/${selectedPromptId}/revisions/${revId}`);
        if (response.data.success) {
          // Update the revision in our list with full content
          setRevisions(prev => prev.map(r => 
            r.id === revId ? { ...r, content: response.data.revision.content } : r
          ));
        }
      } catch (error) {
        console.error("Failed to fetch revision content:", error);
      }
    }
  };

  const startEditing = () => {
    const rev = revisions.find(r => r.id === selectedRevision);
    setEditContent(rev?.content || "");
    setEditName(rev?.name || "");
    setEditRole(rev?.role || promptDetail?.role || "");
    setEditPurpose(rev?.purpose || promptDetail?.purpose || "");
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditContent("");
    setEditName("");
    setEditRole("");
    setEditPurpose("");
  };

  const saveRevision = async () => {
    if (!editContent.trim()) {
      toast.error("Content cannot be empty");
      return;
    }
    
    setSaving(true);
    try {
      const response = await authAxios.put(
        `/system-prompts/prompt/${selectedPromptId}/revisions/${selectedRevision}`,
        { 
          content: editContent, 
          name: editName || undefined,
          role: editRole || undefined,
          purpose: editPurpose || undefined
        }
      );
      
      if (response.data.success) {
        toast.success("Revision saved");
        // Update local state
        setRevisions(prev => prev.map(r => 
          r.id === selectedRevision 
            ? { 
                ...r, 
                content: editContent, 
                name: editName || r.name, 
                role: editRole || r.role,
                purpose: editPurpose || r.purpose,
                token_count: response.data.token_count 
              }
            : r
        ));
        // Also update promptDetail if role/purpose changed
        if (editRole || editPurpose) {
          setPromptDetail(prev => ({
            ...prev,
            role: editRole || prev.role,
            purpose: editPurpose || prev.purpose
          }));
        }
        setIsEditing(false);
        fetchCategories(); // Refresh to update token counts
      }
    } catch (error) {
      console.error("Failed to save revision:", error);
      toast.error(error.response?.data?.detail || "Failed to save revision");
    } finally {
      setSaving(false);
    }
  };

  const createRevision = async () => {
    if (!newRevisionName.trim()) {
      toast.error("Please enter a revision name");
      return;
    }
    
    setCreating(true);
    try {
      // Copy content from currently selected revision (or default)
      const baseContent = getSelectedContent();
      
      const response = await authAxios.post(
        `/system-prompts/prompt/${selectedPromptId}/revisions`,
        { 
          name: newRevisionName,
          content: baseContent,
          based_on: selectedRevision
        }
      );
      
      if (response.data.success) {
        toast.success(`Created revision "${newRevisionName}"`);
        // Add to revisions list
        setRevisions(prev => [{
          ...response.data.revision,
          content: baseContent
        }, ...prev]);
        setShowCreateForm(false);
        setNewRevisionName("");
        // Select the new revision and enter edit mode
        setSelectedRevision(response.data.revision.id);
        setEditContent(baseContent);
        setEditName(newRevisionName);
        setIsEditing(true);
      }
    } catch (error) {
      console.error("Failed to create revision:", error);
      toast.error(error.response?.data?.detail || "Failed to create revision");
    } finally {
      setCreating(false);
    }
  };

  const deleteRevision = async () => {
    if (!deleteConfirm) return;
    
    setDeleting(true);
    try {
      const response = await authAxios.delete(
        `/system-prompts/prompt/${selectedPromptId}/revisions/${deleteConfirm}`
      );
      
      if (response.data.success) {
        toast.success("Revision deleted");
        // Remove from list
        setRevisions(prev => prev.filter(r => r.id !== deleteConfirm));
        // If we deleted the selected revision, go back to default
        if (selectedRevision === deleteConfirm) {
          setSelectedRevision("default");
        }
        setDeleteConfirm(null);
        fetchCategories(); // Refresh
      }
    } catch (error) {
      console.error("Failed to delete revision:", error);
      toast.error(error.response?.data?.detail || "Failed to delete revision");
    } finally {
      setDeleting(false);
    }
  };

  const activateRevision = async (revId) => {
    try {
      let response;
      if (revId === "default") {
        response = await authAxios.post(`/system-prompts/prompt/${selectedPromptId}/activate-default`);
      } else {
        response = await authAxios.post(`/system-prompts/prompt/${selectedPromptId}/revisions/${revId}/activate`);
      }
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Update local state
        setRevisions(prev => prev.map(r => ({
          ...r,
          is_active: r.id === revId
        })));
        fetchCategories(); // Refresh to update active indicators
      }
    } catch (error) {
      console.error("Failed to activate revision:", error);
      toast.error(error.response?.data?.detail || "Failed to activate revision");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const formatTokenCount = (count) => {
    if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
    return count?.toString() || "0";
  };

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-divine-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header & Search */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-cinzel text-divine-gold">System Prompts</h2>
          <p className="text-sm text-gray-400 mt-1">
            View and manage AI prompts with revision history
          </p>
        </div>
        
        <form onSubmit={handleSearch} className="flex gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search prompts..."
              className="pl-10 bg-divine-charcoal border-divine-gold/20 text-white"
              data-testid="prompt-search-input"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Button
            type="submit"
            disabled={searching || !searchQuery.trim()}
            className="bg-divine-gold text-divine-black hover:bg-divine-gold/90"
            data-testid="prompt-search-btn"
          >
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
          </Button>
        </form>
      </div>

      {/* Agent Filter */}
      <div className="flex items-center gap-3 bg-divine-charcoal/50 rounded-lg p-3 border border-divine-gold/10">
        <div className="flex items-center gap-2 text-gray-400">
          <Filter className="h-4 w-4" />
          <span className="text-sm">Filter by Agent:</span>
        </div>
        <Select value={selectedAgent} onValueChange={setSelectedAgent}>
          <SelectTrigger className="w-64 bg-divine-charcoal border-divine-gold/20" data-testid="agent-filter-select">
            <SelectValue placeholder="All Agents" />
          </SelectTrigger>
          <SelectContent className="bg-divine-charcoal border-divine-gold/20 max-h-80">
            <SelectItem value="all" className="text-white">
              <div className="flex items-center gap-2">
                <Cpu className="h-4 w-4 text-divine-gold" />
                <span>All Agents</span>
                <Badge variant="outline" className="ml-2 text-xs text-gray-400 border-gray-600">
                  {categories.reduce((acc, cat) => acc + cat.prompts.length, 0)}
                </Badge>
              </div>
            </SelectItem>
            {allAgents.map((agent) => {
              // Count prompts for this agent
              const promptCount = categories.reduce((acc, cat) => 
                acc + cat.prompts.filter(p => p.agent_name === agent).length, 0
              );
              return (
                <SelectItem key={agent} value={agent} className="text-white">
                  <div className="flex items-center gap-2">
                    <Cpu className="h-4 w-4 text-gray-400" />
                    <span>{agent}</span>
                    <Badge variant="outline" className="ml-2 text-xs text-gray-400 border-gray-600">
                      {promptCount}
                    </Badge>
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        {selectedAgent !== "all" && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedAgent("all")}
            className="text-gray-400 hover:text-white"
          >
            <X className="h-4 w-4 mr-1" />
            Clear
          </Button>
        )}
      </div>

      {/* Search Results */}
      {searchResults && (
        <div className="bg-divine-charcoal rounded-lg border border-divine-gold/20 p-4" data-testid="search-results">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-white">
              Search Results <span className="text-divine-gold">({searchResults.count})</span>
            </h3>
            <Button variant="ghost" size="sm" onClick={clearSearch} className="text-gray-400 hover:text-white">
              Clear
            </Button>
          </div>
          
          {searchResults.results.length === 0 ? (
            <p className="text-gray-400 text-center py-4">No prompts found matching &quot;{searchResults.query}&quot;</p>
          ) : (
            <div className="space-y-2">
              {searchResults.results.map((result) => (
                <div
                  key={result.id}
                  onClick={() => openPromptEditor(result.id)}
                  className="p-3 bg-divine-black/50 rounded border border-divine-gold/10 hover:border-divine-gold/30 cursor-pointer transition-colors"
                  data-testid={`search-result-${result.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-white">{result.title}</p>
                      <p className="text-xs text-gray-400 mt-1">{result.category} &bull; {result.agent_name}</p>
                    </div>
                    <Badge variant="outline" className="text-divine-gold border-divine-gold/30">
                      {formatTokenCount(result.token_count)} tokens
                    </Badge>
                  </div>
                  {result.match_context && (
                    <p className="text-sm text-gray-300 mt-2 bg-divine-charcoal/50 p-2 rounded font-mono text-xs">
                      {result.match_context}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Categories List */}
      {!searchResults && (
        <div className="space-y-4" data-testid="categories-list">
          {filteredCategories.length === 0 ? (
            <div className="bg-divine-charcoal rounded-lg border border-divine-gold/20 p-8 text-center">
              <Cpu className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No prompts found for agent &quot;{selectedAgent}&quot;</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedAgent("all")}
                className="mt-4 text-divine-gold hover:bg-divine-gold/10"
              >
                Clear filter
              </Button>
            </div>
          ) : (
          filteredCategories.map((category) => (
            <div
              key={category.id}
              className="bg-divine-charcoal rounded-lg border border-divine-gold/20 overflow-hidden"
              data-testid={`category-${category.id}`}
            >
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full flex items-center justify-between p-4 hover:bg-divine-gold/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {expandedCategories[category.id] ? (
                    <ChevronDown className="h-5 w-5 text-divine-gold" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-divine-gold" />
                  )}
                  <div className="text-left">
                    <h3 className="font-medium text-white">{category.title}</h3>
                    <p className="text-sm text-gray-400">{category.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="text-gray-300 border-gray-600">
                    {category.prompts.length} prompts
                  </Badge>
                  <Badge variant="outline" className="text-divine-gold border-divine-gold/30">
                    {formatTokenCount(category.prompts.reduce((acc, p) => acc + (p.token_count || 0), 0))} tokens
                  </Badge>
                </div>
              </button>

              {/* Category Prompts */}
              {expandedCategories[category.id] && (
                <div className="border-t border-divine-gold/10">
                  {category.prompts.map((prompt) => (
                    <div
                      key={prompt.id}
                      onClick={() => openPromptEditor(prompt.id)}
                      className="p-4 border-b border-divine-gold/5 last:border-b-0 hover:bg-divine-gold/5 cursor-pointer transition-colors"
                      data-testid={`prompt-item-${prompt.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <FileText className="h-4 w-4 text-divine-gold flex-shrink-0" />
                            <h4 className="font-medium text-white truncate">{prompt.title}</h4>
                            {prompt.active_revision && (
                              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">
                                {prompt.active_revision}
                              </Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400 mb-2">
                            <span className="flex items-center gap-1 font-mono bg-purple-500/20 text-purple-300 px-1.5 py-0.5 rounded border border-purple-500/30">
                              ID: {prompt.id}
                            </span>
                            <span className="flex items-center gap-1">
                              <Cpu className="h-3 w-3" />
                              {prompt.agent_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <ExternalLink className="h-3 w-3" />
                              {prompt.endpoint}
                            </span>
                            <span className="flex items-center gap-1">
                              <Hash className="h-3 w-3" />
                              {formatTokenCount(prompt.token_count)} tokens
                            </span>
                          </div>
                          <p className="text-sm text-gray-300">{prompt.role}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-gray-400 hover:text-divine-gold flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            openPromptEditor(prompt.id);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
          )}
        </div>
      )}

      {/* Prompt Editor Modal */}
      {selectedPromptId && promptDetail && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="prompt-editor-modal">
          <div className="bg-divine-charcoal rounded-lg border border-divine-gold/20 w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-start justify-between p-6 border-b border-divine-gold/10">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="text-xl font-medium text-white">{promptDetail.title}</h2>
                  <span className="font-mono text-sm bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded border border-purple-500/30">
                    ID: {selectedPromptId}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-400">
                  <span className="flex items-center gap-1">
                    <Cpu className="h-4 w-4" />
                    {promptDetail.agent_name}
                  </span>
                  <span className="flex items-center gap-1">
                    <ExternalLink className="h-4 w-4" />
                    {promptDetail.endpoint}
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={closeEditor}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Revision Selector Bar */}
            <div className="flex items-center justify-between gap-4 px-6 py-3 bg-divine-black/30 border-b border-divine-gold/10">
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-400">Revision:</span>
                <Select value={selectedRevision} onValueChange={handleRevisionSelect}>
                  <SelectTrigger className="w-48 bg-divine-charcoal border-divine-gold/20" data-testid="revision-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-divine-charcoal border-divine-gold/20">
                    <SelectItem value="default" className="text-white">
                      <div className="flex items-center gap-2">
                        <span>Default</span>
                        {isActiveRevision("default") && (
                          <Badge className="bg-emerald-500/20 text-emerald-400 text-xs ml-1">Active</Badge>
                        )}
                      </div>
                    </SelectItem>
                    {revisions.map((rev) => (
                      <SelectItem key={rev.id} value={rev.id} className="text-white">
                        <div className="flex items-center gap-2">
                          <span>{rev.name}</span>
                          {rev.is_active && (
                            <Badge className="bg-emerald-500/20 text-emerald-400 text-xs ml-1">Active</Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Token count for selected revision */}
                <Badge variant="outline" className="text-divine-gold border-divine-gold/30">
                  <Hash className="h-3 w-3 mr-1" />
                  {isEditing 
                    ? formatTokenCount(Math.ceil(editContent.length / 4))
                    : formatTokenCount(
                        selectedRevision === "default" 
                          ? promptDetail.token_count 
                          : revisions.find(r => r.id === selectedRevision)?.token_count
                      )
                  } tokens
                </Badge>
              </div>
              
              <div className="flex items-center gap-2">
                {/* Create New Revision */}
                {!showCreateForm && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCreateForm(true)}
                    className="text-divine-gold border-divine-gold/30 hover:bg-divine-gold/10"
                    data-testid="create-revision-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    New Revision
                  </Button>
                )}
                
                {/* Set as Active (only for custom revisions that aren't active) */}
                {!isDefaultSelected && !isActiveRevision(selectedRevision) && !isEditing && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => activateRevision(selectedRevision)}
                    className="text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10"
                    data-testid="activate-revision-btn"
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Set Active
                  </Button>
                )}
                
                {/* Revert to Default (only if a custom revision is active) */}
                {isDefaultSelected && !isActiveRevision("default") && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => activateRevision("default")}
                    className="text-amber-400 border-amber-500/30 hover:bg-amber-500/10"
                    data-testid="revert-default-btn"
                  >
                    <RotateCcw className="h-4 w-4 mr-1" />
                    Revert to Default
                  </Button>
                )}
              </div>
            </div>

            {/* Create Revision Form */}
            {showCreateForm && (
              <div className="px-6 py-3 bg-divine-gold/5 border-b border-divine-gold/10">
                <div className="flex items-center gap-3">
                  <Input
                    value={newRevisionName}
                    onChange={(e) => setNewRevisionName(e.target.value)}
                    placeholder="Revision name (e.g., v2-shorter, experiment-1)"
                    className="flex-1 bg-divine-charcoal border-divine-gold/20 text-white"
                    data-testid="new-revision-name"
                    onKeyDown={(e) => e.key === "Enter" && createRevision()}
                  />
                  <Button
                    size="sm"
                    onClick={createRevision}
                    disabled={creating || !newRevisionName.trim()}
                    className="bg-divine-gold text-divine-black hover:bg-divine-gold/90"
                    data-testid="confirm-create-btn"
                  >
                    {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewRevisionName("");
                    }}
                    className="text-gray-400"
                  >
                    Cancel
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  New revision will be created with content from: {selectedRevision === "default" ? "Default" : revisions.find(r => r.id === selectedRevision)?.name}
                </p>
              </div>
            )}

            {/* Modal Body - Content Area */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Role & Purpose */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-divine-black/50 rounded p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="h-4 w-4 text-divine-gold" />
                    <h3 className="font-medium text-divine-gold">Role</h3>
                  </div>
                  {isEditing ? (
                    <Textarea
                      value={editRole}
                      onChange={(e) => setEditRole(e.target.value)}
                      placeholder="Enter role description..."
                      className="bg-divine-charcoal border-divine-gold/20 text-gray-300 text-sm min-h-[80px]"
                      data-testid="edit-role"
                    />
                  ) : (
                    <p className="text-sm text-gray-300">{promptDetail.role}</p>
                  )}
                </div>
                <div className="bg-divine-black/50 rounded p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-4 w-4 text-divine-gold" />
                    <h3 className="font-medium text-divine-gold">Purpose</h3>
                  </div>
                  {isEditing ? (
                    <Textarea
                      value={editPurpose}
                      onChange={(e) => setEditPurpose(e.target.value)}
                      placeholder="Enter purpose description..."
                      className="bg-divine-charcoal border-divine-gold/20 text-gray-300 text-sm min-h-[80px]"
                      data-testid="edit-purpose"
                    />
                  ) : (
                    <p className="text-sm text-gray-300">{promptDetail.purpose}</p>
                  )}
                </div>
              </div>

              {/* Prompt Content */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-divine-gold">
                      {isEditing ? "Edit Content" : "Prompt Content"}
                    </h3>
                    {isDefaultSelected && !isEditing && (
                      <Badge variant="outline" className="text-gray-400 border-gray-600 text-xs">
                        Default (create revision to edit)
                      </Badge>
                    )}
                    {isActiveRevision(selectedRevision) && (
                      <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">
                        Currently Active
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {/* Edit button - for custom revisions */}
                    {!isDefaultSelected && !isEditing && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={startEditing}
                        className="text-divine-gold border-divine-gold/30 hover:bg-divine-gold/10"
                        data-testid="edit-revision-btn"
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    )}
                    
                    {/* Edit button - for default (creates a revision) */}
                    {isDefaultSelected && !isEditing && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // Pre-fill the revision name and open create form
                          const timestamp = new Date().toISOString().split('T')[0];
                          setNewRevisionName(`edit-${timestamp}`);
                          setShowCreateForm(true);
                        }}
                        className="text-divine-gold border-divine-gold/30 hover:bg-divine-gold/10"
                        data-testid="edit-default-btn"
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        Edit (New Revision)
                      </Button>
                    )}
                    
                    {/* Delete button (only for custom revisions, not when editing) */}
                    {!isDefaultSelected && !isEditing && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteConfirm(selectedRevision)}
                        className="text-red-400 border-red-500/30 hover:bg-red-500/10"
                        data-testid="delete-revision-btn"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    )}
                    
                    {/* Copy button */}
                    {!isEditing && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(getSelectedContent())}
                        className="text-divine-gold border-divine-gold/30 hover:bg-divine-gold/10"
                        data-testid="copy-prompt-btn"
                      >
                        <Copy className="h-4 w-4 mr-1" />
                        Copy
                      </Button>
                    )}
                  </div>
                </div>
                
                {isEditing ? (
                  <div className="space-y-3">
                    {/* Revision name edit */}
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Revision Name</label>
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="bg-divine-black border-divine-gold/20 text-white"
                        data-testid="edit-revision-name"
                      />
                    </div>
                    {/* Content edit */}
                    <Textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="bg-divine-black border-divine-gold/20 text-gray-300 font-mono text-sm min-h-[400px]"
                      data-testid="edit-revision-content"
                    />
                  </div>
                ) : (
                  <pre className="bg-divine-black rounded p-4 text-sm text-gray-300 whitespace-pre-wrap font-mono overflow-x-auto border border-divine-gold/10 max-h-[400px] overflow-y-auto">
                    {getSelectedContent()}
                  </pre>
                )}
              </div>

              {/* Revision Info (when viewing custom revision) */}
              {!isDefaultSelected && !isEditing && (
                <div className="text-xs text-gray-500 flex items-center gap-4">
                  {revisions.find(r => r.id === selectedRevision)?.updated_at && (
                    <span>Last updated: {formatDate(revisions.find(r => r.id === selectedRevision)?.updated_at)}</span>
                  )}
                  {revisions.find(r => r.id === selectedRevision)?.created_by && (
                    <span>Created by: {revisions.find(r => r.id === selectedRevision)?.created_by}</span>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-4 border-t border-divine-gold/10">
              {isEditing ? (
                <>
                  <Button
                    variant="outline"
                    onClick={cancelEditing}
                    className="text-gray-300 border-gray-600 hover:bg-gray-700"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={saveRevision}
                    disabled={saving}
                    className="bg-divine-gold text-divine-black hover:bg-divine-gold/90"
                    data-testid="save-revision-btn"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                    Save Changes
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  onClick={closeEditor}
                  className="text-gray-300 border-gray-600 hover:bg-gray-700"
                >
                  Close
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay for Prompt Editor */}
      {loadingPrompt && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-divine-gold" />
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent className="bg-divine-charcoal border-divine-gold/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Revision?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This will permanently delete the revision &quot;{revisions.find(r => r.id === deleteConfirm)?.name}&quot;. 
              {isActiveRevision(deleteConfirm) && (
                <span className="text-amber-400 block mt-2">
                  Warning: This revision is currently active. Deleting it will revert to the Default prompt.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-gray-600 text-gray-300 hover:bg-gray-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteRevision}
              disabled={deleting}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
